/**
 * CommandPalette (DESIGN-SPRINT-01) — Ctrl/Cmd+K from anywhere.
 *
 * The modern-tool signature interaction (Linear/Stripe/Slack): one keystroke,
 * type anything, land anywhere. v1 scope: jump to any screen + the universal
 * search (customers + dispatches) the header already runs — same hooks, same
 * caches, zero new deps.
 *
 * ui-ux-pro-max rules applied: combobox/listbox ARIA with
 * aria-activedescendant; focus moves into the input on open and RETURNS to
 * the triggering element on close (escape-routes); Esc + outside-click + ✕
 * (the Pegasus modal contract); selection never color-only (accent bar +
 * background); transform/opacity-only motion ≤200ms honoring
 * prefers-reduced-motion; tabular hint footer; no emoji icons (Lucide).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  CalendarDays,
  ClipboardList,
  Hammer,
  Inbox,
  LayoutGrid,
  Megaphone,
  Package,
  Receipt,
  Search,
  Settings,
  ShieldCheck,
  Truck,
  User,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { useUniversalSearch } from "@/features/universal-search/useUniversalSearch";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { routeCaps, SETTINGS_ANY } from "@/lib/permissions/routeCaps";

interface NavCommand {
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  keywords: string;
}

const NAV_COMMANDS: NavCommand[] = [
  { label: "Customers", path: "/customers", icon: Users, keywords: "find search 360" },
  { label: "Inbox", path: "/inbox", icon: Inbox, keywords: "messages texts email podium" },
  { label: "Board", path: "/board", icon: LayoutGrid, keywords: "dispatch schedule techs map cockpit" },
  { label: "Today", path: "/today", icon: CalendarDays, keywords: "briefing morning on-call triage" },
  { label: "Memberships", path: "/agreements", icon: ShieldCheck, keywords: "agreements plans members renewals visits plus" },
  { label: "Jobs", path: "/jobs", icon: Hammer, keywords: "installs new construction sold" },
  { label: "Pricebook", path: "/items", icon: Package, keywords: "items pricebook parts materials inventory" },
  { label: "Vendors", path: "/vendors", icon: Truck, keywords: "suppliers purchasing" },
  { label: "Purchase Orders", path: "/purchase-orders", icon: Receipt, keywords: "po receiving" },
  { label: "Marketing", path: "/marketing", icon: Megaphone, keywords: "campaigns email sms" },
  { label: "Reports", path: "/reports", icon: ClipboardList, keywords: "insights revenue analytics" },
  { label: "Settings", path: "/settings", icon: Settings, keywords: "admin users roles pricing rules" },
];

type Item =
  | { kind: "nav"; id: string; cmd: NavCommand }
  | { kind: "customer"; id: string; custno: string; name: string; sub: string }
  | { kind: "dispatch"; id: string; dispatch: string; name: string; sub: string };

const DEBOUNCE_MS = 200;

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const [raw, setRaw] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState(0);

  // Debounce the live search; nav filtering uses the raw value (instant).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [raw]);

  const search = useUniversalSearch(debounced);

  // Focus discipline: capture the trigger, focus the input, restore on close.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      setRaw("");
      setDebounced("");
      setSelected(0);
      // Belt-and-suspenders focus: the panel mounts inside AnimatePresence and
      // the triggering click can re-steal focus a tick later — focus now, next
      // frame, and once more after the click settles.
      const t1 = requestAnimationFrame(() => inputRef.current?.focus());
      const t2 = setTimeout(() => inputRef.current?.focus(), 80);
      return () => {
        cancelAnimationFrame(t1);
        clearTimeout(t2);
      };
    } else {
      restoreFocusRef.current?.focus?.();
    }
  }, [open]);

  const q = raw.trim().toLowerCase();
  // PERM-ENFORCE-01c: the palette offers only pages this login can open (same
  // map as the router and the rail — routeCaps).
  const { canAny } = usePermissions();
  const navMatches = useMemo(
    () =>
      NAV_COMMANDS.filter(
        (c) =>
          canAny(c.path === "/settings" ? SETTINGS_ANY : routeCaps(c.path)) &&
          (q.length === 0 ||
          c.label.toLowerCase().includes(q) ||
          c.keywords.includes(q)),
      ),
    [q, canAny],
  );

  const items: Item[] = useMemo(() => {
    const out: Item[] = navMatches.map((cmd) => ({
      kind: "nav",
      id: `nav:${cmd.path}`,
      cmd,
    }));
    for (const c of search.customers.rows) {
      // Same primary-location pick as the header dropdown (skip inactive).
      const loc =
        c.locations?.find((l) => !l.location_inactive) ?? c.locations?.[0];
      out.push({
        kind: "customer",
        id: `cust:${c.custno}`,
        custno: c.custno,
        name: displayCustomerName(null, c.fullname) || c.custno,
        sub: [loc?.add1, loc?.city].filter(Boolean).join(", "),
      });
    }
    for (const d of search.dispatches.rows) {
      out.push({
        kind: "dispatch",
        id: `disp:${d.dispatch}`,
        dispatch: d.dispatch,
        name: `Dispatch ${d.dispatch}`,
        sub: [
          displayCustomerName(d.locname, d.customer_fullname),
          d.appointment_date,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }
    return out;
  }, [navMatches, search.customers.rows, search.dispatches.rows]);

  // Clamp selection as results change.
  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, items.length - 1)));
  }, [items.length]);

  const run = useCallback(
    (item: Item) => {
      onClose();
      if (item.kind === "nav") navigate(item.cmd.path);
      else if (item.kind === "customer") navigate(`/customers/${item.custno}`);
      else navigate(`/dispatch/${item.dispatch}`);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(items.length - 1, s + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(0, s - 1));
    } else if (e.key === "Enter" && items[selected]) {
      e.preventDefault();
      run(items[selected]);
    }
  };

  // Keep the selected row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selected}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const sectionLabel = (kind: Item["kind"], idx: number) => {
    const first = items.findIndex((i) => i.kind === kind);
    if (first !== idx) return null;
    const label =
      kind === "nav" ? "Go to" : kind === "customer" ? "Customers" : "Dispatches";
    return (
      <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-[90] bg-slate-900/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <div className="pointer-events-none fixed inset-0 z-[90] flex items-start justify-center px-4 pt-[18vh]">
            <motion.div
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.98 }}
              animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.16, ease: [0.25, 1, 0.5, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label="Command palette"
              className="pointer-events-auto w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-900/5"
            >
              {/* input row */}
              <div className="flex items-center gap-2.5 border-b border-hairline px-4">
                <Search className="h-4 w-4 shrink-0 text-slate-400" />
                <input
                  ref={inputRef}
                  value={raw}
                  onChange={(e) => {
                    setRaw(e.target.value);
                    setSelected(0);
                  }}
                  onKeyDown={onKeyDown}
                  role="combobox"
                  aria-expanded="true"
                  aria-controls="palette-listbox"
                  aria-activedescendant={items[selected]?.id}
                  aria-label="Search customers, dispatches, or jump to a screen"
                  placeholder="Search customers, dispatch # — or jump to a screen…"
                  className="h-12 w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* results */}
              <div
                id="palette-listbox"
                role="listbox"
                ref={listRef}
                className="max-h-[46vh] overflow-y-auto pb-2"
              >
                {items.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-500">
                    {search.isLoading
                      ? "Searching…"
                      : `Nothing matches “${raw.trim()}” — try a name, phone, address, or dispatch #.`}
                  </p>
                ) : (
                  items.map((item, idx) => {
                    const active = idx === selected;
                    const Icon =
                      item.kind === "nav"
                        ? item.cmd.icon
                        : item.kind === "customer"
                          ? User
                          : Wrench;
                    return (
                      <div key={item.id}>
                        {sectionLabel(item.kind, idx)}
                        <div
                          id={item.id}
                          data-idx={idx}
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setSelected(idx)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => run(item)}
                          className={cn(
                            "mx-2 flex cursor-pointer items-center gap-3 rounded-lg border-l-[3px] px-3 py-2",
                            active
                              ? "border-brand-600 bg-brand-50"
                              : "border-transparent",
                          )}
                        >
                          <Icon
                            className={cn(
                              "h-4 w-4 shrink-0",
                              active ? "text-brand-700" : "text-slate-400",
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div
                              className={cn(
                                "truncate text-sm",
                                active
                                  ? "font-medium text-slate-900"
                                  : "text-slate-700",
                              )}
                            >
                              {item.kind === "nav" ? item.cmd.label : item.name}
                            </div>
                            {item.kind !== "nav" && item.sub && (
                              <div className="truncate text-xs text-slate-500">
                                {item.sub}
                              </div>
                            )}
                          </div>
                          {item.kind === "nav" && (
                            <span className="text-[11px] text-slate-400">
                              Jump to
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* hint footer */}
              <div className="flex items-center gap-3 border-t border-hairline bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
                <span>
                  <kbd className="rounded border border-slate-300 bg-white px-1">↑↓</kbd>{" "}
                  navigate
                </span>
                <span>
                  <kbd className="rounded border border-slate-300 bg-white px-1">↵</kbd>{" "}
                  open
                </span>
                <span>
                  <kbd className="rounded border border-slate-300 bg-white px-1">esc</kbd>{" "}
                  close
                </span>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
